import { type ReactNode } from "react";
import { Anchor, FileCheck, FileText, UserPlus } from "lucide-react";
import { type CaseDetail } from "@/lib/api";

interface TimelineEvent {
  key: string;
  date: string;
  title: string;
  subtitle?: string;
  icon: ReactNode;
  color: string;
}

// Construit la liste d'evenements traceables d'un dossier a partir de la
// reponse `getCaseDetail`. P1 : derive cote front (pas d'endpoint dedie).
// P2 : a remplacer par un GET /api/v1/cases/{id}/events qui agregera
// transitions workflow, decisions creees/signees, deadlines, etc.
function buildEvents(detail: CaseDetail): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  if (detail.date_creation) {
    events.push({
      key: `case-open-${detail.id}`,
      date: detail.date_creation,
      title: "Dossier ouvert",
      subtitle: detail.numero_gsf,
      icon: <FileText size={11} />,
      color: "var(--justice-blue)",
    });
  }

  detail.members.forEach((m, i) => {
    if (!m.date_creation) return;
    events.push({
      key: `member-${i}-${m.user_id}`,
      date: m.date_creation,
      title: `${m.member_role} constitué`,
      subtitle:
        m.user_id.length > 18
          ? `${m.user_id.slice(0, 8)}…${m.user_id.slice(-4)}`
          : m.user_id,
      icon: <UserPlus size={11} />,
      color: "var(--arbitre-slate-300)",
    });
  });

  detail.documents.forEach((d) => {
    if (!d.date_creation) return;
    events.push({
      key: `doc-${d.id}`,
      date: d.date_creation,
      title: "Pièce probatoire déposée",
      subtitle:
        d.original_filename +
        (d.role_at_upload ? ` · ${d.role_at_upload}` : ""),
      icon: <FileCheck size={11} />,
      color: "var(--proof-gold)",
    });
  });

  if (detail.anchored_at) {
    const status = detail.anchored_status;
    events.push({
      key: `anchor-${detail.id}-${detail.blockchain_tx_hash ?? "none"}`,
      date: detail.anchored_at,
      title:
        status === "SUCCESS"
          ? "Ancrage Polygon confirmé"
          : status === "PENDING"
            ? "Ancrage Polygon en cours"
            : status === "FAILED"
              ? "Ancrage Polygon échoué"
              : "Ancrage Polygon",
      subtitle: detail.blockchain_tx_hash
        ? `${detail.blockchain_tx_hash.slice(0, 10)}…${detail.blockchain_tx_hash.slice(-6)}`
        : undefined,
      icon: <Anchor size={11} />,
      color:
        status === "SUCCESS"
          ? "var(--validate-green)"
          : status === "PENDING"
            ? "var(--alert-orange)"
            : status === "FAILED"
              ? "var(--block-red)"
              : "var(--arbitre-slate-500)",
    });
  }

  // Tri descendant : evenement le plus recent en haut.
  return events.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
}

// Timeline verticale historique d'un dossier. Distincte de la frise
// procedurale horizontale (ProcedureTimeline) qui montre l'etat
// procedural courant (OUVERT -> ... -> TERMINE). Ici on retrace les
// evenements DATES de la chaine de custody : creation, membres,
// pieces, ancrages.
//
// Style : ligne verticale graphite-600, points colores par categorie
// d'evenement, cards a droite avec date + titre + sous-titre mono pour
// les hashes / IDs.
export function CaseTimeline({ detail }: { detail: CaseDetail }) {
  const events = buildEvents(detail);

  if (events.length === 0) {
    return (
      <p
        className="text-sm italic"
        style={{ color: "var(--arbitre-slate-500)" }}
      >
        Aucun événement à afficher pour ce dossier.
      </p>
    );
  }

  return (
    <ol className="relative" style={{ paddingLeft: "1.5rem" }}>
      <span
        className="absolute top-1 bottom-1 w-px"
        style={{ left: "0.4375rem", background: "var(--graphite-600)" }}
        aria-hidden
      />
      {events.map((e, i) => (
        <li
          key={e.key}
          className="relative"
          style={{ marginTop: i > 0 ? "1rem" : 0 }}
        >
          <span
            className="absolute flex items-center justify-center rounded-full"
            style={{
              left: "-1.125rem",
              top: "0.0625rem",
              width: "0.9375rem",
              height: "0.9375rem",
              background: e.color,
              boxShadow: "0 0 0 3px var(--graphite-800)",
              color: "var(--graphite-900)",
            }}
            aria-hidden
          >
            {e.icon}
          </span>
          <div
            className="text-caption-arbitre"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            {fmtDateTime(e.date)}
          </div>
          <div className="text-sm font-medium mt-0.5">{e.title}</div>
          {e.subtitle && (
            <div
              className="text-xs gsf-mono-proof mt-0.5 break-all"
              style={{ color: "var(--arbitre-slate-300)" }}
            >
              {e.subtitle}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
